import { Shimmer, ShimmerElementType, Stack, Text } from "@fluentui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PRODUCT_VIEW_STYLES as styles } from "../../components/ProductView/Common/ProductView.styles";
import { logEvent, logPageView } from "../../helper/AppInsightsHelper";
import { PromptLibraryItem } from "../../interfaces/slices.interfaces";
import CopilotChat from "../../pages/CopilotChat";
import { useGetPromptLibraryQuery } from "../../slices/AIHub.slice";
import { SectionDivider } from "./Components/Common";
import { NavigationTabs } from "./Components/Navigation";
import { PromptCardGrid, PromptPackSection } from "./Components/PromptLibrary";
import { SessionsPage } from "./Components/Sessions";
import PromptPlayground from "./PromptPlayground";

export default function AIPlayground() {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultSection = "home";
  // If share param is present, default to kqlmode (Prompt Playground) to load the shared data
  const hasShareParam = searchParams.has("share");
  const defaultTab = hasShareParam ? "kqlmode"
    : searchParams.has("sessionId") || searchParams.has("scenarioId") || searchParams.has("scenarioGroup")
      ? "copilotchat" : "cvciq";

  const [selectedTab, setSelectedTab] = useState(searchParams.get("tab") ?? defaultTab);
  const [selectedNestedTab, setSelectedNestedTab] = useState(searchParams.get("section") ?? defaultSection);
  const [selectedPrompt, setSelectedPrompt] = useState<PromptLibraryItem | undefined>();
  const { data, isLoading } = useGetPromptLibraryQuery();
  const topPrompts = data?.filter((item) => item.tags?.split(";").includes("featured")) ?? [];

  // Group prompts by scenarioGrouping for the library view
  const groupedPrompts = useMemo(() => {
    if (!data) return { grouped: {}, ungrouped: [] };

    const grouped: Record<string, PromptLibraryItem[]> = {};
    const ungrouped: PromptLibraryItem[] = [];

    data.forEach((prompt) => {
      const scenarioGroup = prompt.scenarioGrouping?.trim();
      if (scenarioGroup) {
        if (!grouped[scenarioGroup]) {
          grouped[scenarioGroup] = [];
        }
        grouped[scenarioGroup].push(prompt);
      } else {
        ungrouped.push(prompt);
      }
    });

    return { grouped, ungrouped };
  }, [data]);

  // Ref for PromptPlayground
  const aiPlaygroundContainerRef = useRef<HTMLDivElement>(null);
  const playgroundRef = useRef<{ runPrompt: () => void }>(null);

  useEffect(() => {
    logPageView("AI Hub", window.location.href);
  }, []);

  // Initialize URL parameters with defaults if not present
  useEffect(() => {
    const tab = searchParams.get("tab");
    const section = searchParams.get("section");
    const shareId = searchParams.get("share");

    if (!tab) {
      // Set default tab in URL, preserving share param if present
      const params = new URLSearchParams(searchParams);
      params.set("tab", defaultTab);
      if (defaultTab === "kqlmode") {
        params.set("section", section ?? defaultSection);
      }
      setSearchParams(params);
    } else if (tab === "kqlmode" && !section) {
      // If KQL Mode is selected but no section, add default section
      const params: Record<string, string> = { tab: tab, section: defaultSection };
      if (shareId) {
        params.share = shareId;
      }
      setSearchParams(params);
    }
  }, []);

  // Update URL when tab changes
  useEffect(() => {
    const currentTab = searchParams.get("tab");
    if (currentTab !== selectedTab) {
      if (selectedTab === "kqlmode") {
        setSearchParams({ tab: selectedTab, section: selectedNestedTab });
      } else {
        setSearchParams({ tab: selectedTab });
      }
    }
  }, [selectedTab, selectedNestedTab]);

  // Update URL when section changes
  useEffect(() => {
    const currentSection = searchParams.get("section");
    if (selectedTab === "kqlmode" && currentSection !== selectedNestedTab) {
      setSearchParams({ tab: selectedTab, section: selectedNestedTab });
    }
  }, [selectedTab, selectedNestedTab]);

  // Log when user opens the prompt library tab
  useEffect(() => {
    if (selectedTab === "kqlmode" && selectedNestedTab === "library") {
      logEvent("Prompt Library Opened", "AI Hub - KQL Mode", "AIPlayground");
    }
  }, [selectedTab, selectedNestedTab]);

  const onRunPrompt = (prompt: PromptLibraryItem, action: "run" | "open") => {
    setSelectedTab("kqlmode");
    setSelectedNestedTab("home");
    setSelectedPrompt(prompt);
    // Update URL parameters
    setSearchParams({ tab: "kqlmode", section: "home" });
    // Scroll to PromptPlayground after state updates
    setTimeout(() => {
      aiPlaygroundContainerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
      if (action === "run") playgroundRef.current?.runPrompt();
    }, 100);
  };

  return (
    <div style={{ height: "100%" }}>
      <div style={{ padding: "1em 24px 0 0px" }}>
        <Text variant="xxLarge" styles={{ root: { fontWeight: 600, color: "#005597" } }}>
          Welcome to Security360 AI Hub - Play with Prompts. Build with Intelligence.
        </Text>


        {(isLoading && (
          <div className={styles.PRODUCT_LIST.CLASS_NAMES.selectedFamilyShimmerContainer}>
            <Stack horizontal wrap styles={styles.PRODUCT_LIST.CSS_STYLES.currentSelectedFamilyShimmerStack}>
              {Array.from({ length: 6 }).map(() => (
                <Shimmer
                  key={`loading-${crypto.randomUUID()}`}
                  shimmerElements={[{ type: ShimmerElementType.line, height: 32 }]}
                  styles={styles.PRODUCT_LIST.CSS_STYLES.familyToggleButtonsShimmer}
                />
              ))}
            </Stack>
          </div>
        )) || (
            <div style={{ marginTop: "15px", marginLeft: "15px" }}>
              <NavigationTabs
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                selectedNestedTab={selectedNestedTab}
                setSelectedNestedTab={setSelectedNestedTab}
              />
            </div>
          )}
        {selectedTab === "kqlmode" && selectedNestedTab === "home" && <SectionDivider text={"Most Popular Prompts"} />}
        {selectedTab === "kqlmode" && selectedNestedTab === "library" && <SectionDivider text={"Prompt Library"} />}
        {selectedTab === "kqlmode" && selectedNestedTab === "sessions" && <SectionDivider text={"My Prompts"} />}
        {selectedTab === "kqlmode" && selectedNestedTab === "home" && (
          <>
            <PromptCardGrid prompts={topPrompts} onRunPrompt={onRunPrompt} useCarousel={true} />
            <div ref={aiPlaygroundContainerRef}>
              <PromptPlayground ref={playgroundRef} selectedPrompt={selectedPrompt} />
            </div>
          </>
        )}
        {selectedTab === "kqlmode" && selectedNestedTab === "library" && (
          <>
            {/* Show prompts grouped by scenarioGrouping with category filters */}
            {Object.entries(groupedPrompts.grouped).map(([scenarioGrouping, prompts]) => (
              <PromptPackSection
                key={scenarioGrouping}
                scenarioGrouping={scenarioGrouping}
                prompts={prompts}
                onRunPrompt={onRunPrompt}
              />
            ))}
            {/* Show ungrouped prompts in the original card grid */}
            {groupedPrompts.ungrouped.length > 0 && (
              <>
                {Object.keys(groupedPrompts.grouped).length > 0 && (
                  <SectionDivider text={"Other Prompts"} />
                )}
                <PromptCardGrid prompts={groupedPrompts.ungrouped} onRunPrompt={onRunPrompt} />
              </>
            )}
          </>
        )}
        {selectedTab === "kqlmode" && selectedNestedTab === "sessions" && <SessionsPage onRunPrompt={onRunPrompt} />}
        {selectedTab === "cvciq" && <CopilotChat key="cvciq" cvcIq />}
        {selectedTab === "copilotchat" && <CopilotChat key="copilotchat" />}
      </div>
    </div>
  );
}
